import { useOwnerParams } from 'hooks/use-owner-params.hook';
import { useSearchParams } from 'hooks/use-search-params.hook';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ACTION_STATUSES } from 'shared/consts';
import { getInfiniteResources } from 'store/resources/actions';
import {
  selectGetInfiniteResourcesStatus,
  selectHasMoreInfiniteResources,
  selectInfiniteResources,
  selectInfiniteResourcesTotal,
  selectResources,
} from 'store/resources/selectors';
import { FilterParams } from 'store/resources/types';
import { getFilterParams } from 'utils';
import { useIsSmallViewportWidth } from 'hooks/use-is-small-viewport-width.hook';
import { getOffsetElements } from 'pages/resources/utils/getOffsetElements';

export const useInfiniteContent = (
  scrollContainerRef: React.MutableRefObject<HTMLDivElement>,
): { limitOfElements: number } => {
  const isMobileDevice = useIsSmallViewportWidth();
  const dispatch = useDispatch();
  const infiniteResources = useSelector(selectInfiniteResources);
  const infiniteResourcesTotal = useSelector(selectInfiniteResourcesTotal);
  const getInfiniteResourcesStatus = useSelector(selectGetInfiniteResourcesStatus);
  const resources = useSelector(selectResources);
  const { ...queryParams } = useSearchParams();
  const { filterByType, startDate, startTime, endTime, duration, capacity, filterByFloor, presetWindows } = queryParams;
  const { ownerType, ownerUuid } = useOwnerParams();
  const isInfiniteResourcesLoading = getInfiniteResourcesStatus.status === ACTION_STATUSES.PENDING;
  const swipeModalScrollContainer = document.getElementById('scroll-container');
  const hasMoreInfiniteResources = useSelector(selectHasMoreInfiniteResources);

  const limitOfElements = useMemo<number>(() => {
    const scrollElement = scrollContainerRef?.current || swipeModalScrollContainer;
    if (!scrollElement?.clientHeight) {
      return 0;
    }

    return getOffsetElements(isMobileDevice, scrollElement.clientHeight);
  }, [scrollContainerRef, swipeModalScrollContainer, isMobileDevice]);

  const numberDownloadedResources = useMemo(
    () => Object.values(infiniteResources).flat().length + limitOfElements,
    [limitOfElements, infiniteResources],
  );

  const onScroll = useCallback((): void => {
    const scrollElement = scrollContainerRef?.current || swipeModalScrollContainer;
    if (!scrollElement) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollElement;
    const limit = limitOfElements || getOffsetElements(isMobileDevice, scrollElement.clientHeight);
    const resourcesLimit = limit < resources?.length ? resources?.length : limit;

    if (clientHeight + scrollTop > scrollHeight - 10 && !isInfiniteResourcesLoading && hasMoreInfiniteResources) {
      const searchParams = {
        types: filterByType,
        startDate,
        startTime,
        endTime,
        duration,
        capacity: +capacity,
        floor: filterByFloor,
        presetWindows,
      } as FilterParams;
      const filterParams = getFilterParams({ ...searchParams });

      const offset =
        numberDownloadedResources < infiniteResourcesTotal
          ? resourcesLimit * (Object.keys(infiniteResources)?.length ?? 0)
          : infiniteResourcesTotal;

      dispatch(
        getInfiniteResources.request({
          ownerType,
          ownerUuid,
          filterParams: {
            ...filterParams,
            limit: resourcesLimit,
            offset,
          },
          indexOfSet: (Object.keys(infiniteResources)?.length ?? 0) + 1,
        }),
      );
    }
  }, [
    scrollContainerRef,
    swipeModalScrollContainer,
    limitOfElements,
    isMobileDevice,
    resources?.length,
    isInfiniteResourcesLoading,
    hasMoreInfiniteResources,
    filterByType,
    startDate,
    startTime,
    endTime,
    duration,
    capacity,
    filterByFloor,
    presetWindows,
    numberDownloadedResources,
    infiniteResourcesTotal,
    infiniteResources,
    dispatch,
    ownerType,
    ownerUuid,
  ]);

  useEffect(() => {
    const scrollElement = scrollContainerRef?.current || swipeModalScrollContainer;

    if (limitOfElements < infiniteResourcesTotal && scrollElement) {
      scrollElement.addEventListener('scroll', onScroll);
    }

    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener('scroll', onScroll);
      }
    };
  }, [onScroll, scrollContainerRef, limitOfElements, infiniteResourcesTotal, swipeModalScrollContainer]);

  useEffect(() => {
    const scrollElement = scrollContainerRef?.current || swipeModalScrollContainer;
    if (!scrollElement) return;

    const resourcesLength = Object.values(infiniteResources).flat().length;
    const limit = limitOfElements || getOffsetElements(isMobileDevice, scrollElement.clientHeight);

    if (limit > resourcesLength && !isInfiniteResourcesLoading && hasMoreInfiniteResources) {
      const searchParams = {
        types: filterByType,
        startDate,
        startTime,
        endTime,
        duration,
        capacity: +capacity,
        floor: filterByFloor,
        presetWindows,
      } as FilterParams;
      const filterParams = getFilterParams({ ...searchParams });
      const currentPage = Object.keys(infiniteResources)?.length ?? 0;
      const offset = currentPage * limit;
      const resourcesLimit = limit < resources?.length ? resources?.length : limit;

      dispatch(
        getInfiniteResources.request({
          ownerType,
          ownerUuid,
          filterParams: {
            ...filterParams,
            limit: resourcesLimit,
            offset,
          },
          indexOfSet: currentPage + 1,
        }),
      );
    }
  }, [
    capacity,
    dispatch,
    duration,
    endTime,
    filterByFloor,
    filterByType,
    hasMoreInfiniteResources,
    infiniteResources,
    isInfiniteResourcesLoading,
    isMobileDevice,
    limitOfElements,
    ownerType,
    ownerUuid,
    presetWindows,
    resources?.length,
    scrollContainerRef,
    startDate,
    startTime,
    swipeModalScrollContainer,
  ]);

  return { limitOfElements };
};
